import { useState, useEffect, useCallback } from 'react';
import { announcementService, propertyService, assemblyService } from 'services/api';
import PropertyCombobox from 'components/PropertyCombobox';
import { useAuth } from 'context/AuthContext';
import ComunicadoFormModal from './ComunicadoFormModal';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import ConfirmActionModal from 'components/ConfirmActionModal';
import useCierreAlerta from 'utils/useCierreAlerta';
import styles from './Comunicados.module.css';

const fmtDate = (d) => d ? new Date(d).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' }) : '';

function estado(a) {
  if (!a.published_at) return { label: 'Borrador', color: '#8a92a6' };
  if (a.emailed_at)    return { label: 'Publicado + correo', color: '#12b76a' };
  return { label: 'Publicado', color: '#0ea5e9' };
}

export default function Comunicados() {
  const { isSuperAdmin, user } = useAuth();
  const [items, setItems] = useState(null);
  const [properties, setProperties] = useState([]);
  const [filterProp, setFilterProp] = useState('');
  const [editor, setEditor] = useState(null); // null | {} | announcement
  const [error, setError] = useState('');
  const [alert, setAlert] = useState(null);
  const cerrarAlertaEn = useCierreAlerta(setAlert);
  const [myAsms, setMyAsms] = useState([]);
  const [confirmDel,  setConfirmDel]  = useState(null);
  const [confirmSend, setConfirmSend] = useState(null);
  const [busyModal,   setBusyModal]   = useState(false);
  const isAdmin = user?.role === 'admin';
  const isAsmAdmin = user?.role === 'assembly_admin';

  const showAlert = (type, message) => { setAlert({ type, message }); cerrarAlertaEn(3500); };

  const load = useCallback(async () => {
    try {
      const params = filterProp ? { property_id: filterProp } : {};
      const r = await announcementService.getAll(params);
      setItems(r.data.announcements || []);
    } catch (e) { setError(e.response?.data?.message || 'No se pudieron cargar los comunicados.'); }
  }, [filterProp]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isSuperAdmin || isAdmin) {
      propertyService.selector({ limit: 200 }).then(r => setProperties(r.data.data || [])).catch(() => {});
    }
    if (isAsmAdmin) {
      assemblyService.getAll({ limit: 50 }).then(r => setMyAsms(r.data.data || [])).catch(() => {});
    }
  }, [isSuperAdmin, isAdmin, isAsmAdmin]);

  const act = async (fn, okMsg) => {
    try { await fn(); await load(); if (okMsg) showAlert('success', okMsg); }
    catch (e) { showAlert('danger', e.response?.data?.message || 'No se pudo completar la acción.'); }
  };

  const confirmDelete = async () => {
    if (!confirmDel) return;
    setBusyModal(true);
    await act(() => announcementService.delete(confirmDel.id), 'Comunicado eliminado');
    setBusyModal(false);
    setConfirmDel(null);
  };

  const confirmSendEmail = async () => {
    if (!confirmSend) return;
    setBusyModal(true);
    await act(() => announcementService.publish(confirmSend.id, true), 'Envío por correo iniciado');
    setBusyModal(false);
    setConfirmSend(null);
  };

  // Conjuntos donde ESTE admin puede publicar: el plan "Asamblea + Logística"
  // no incluye comunicados, así que no debe salir en el selector (el backend
  // igual lo rechaza, pero no hay que dar la sensación de que se puede).
  // super_admin publica en cualquiera.
  const publicables = isSuperAdmin ? properties
    : properties.filter(p => p.subscription_type !== 'logistica');
  // Conjuntos que cambiaron a un plan sin comunicados — sus comunicados viejos
  // siguen listados pero el admin ya no puede tocarlos (el backend los rechaza).
  const logisticaIds = new Set(properties.filter(p => p.subscription_type === 'logistica').map(p => p.id));

  const fixedPropertyId = (!isSuperAdmin && publicables.length === 1) ? publicables[0].id
                        : (!isSuperAdmin && !isAdmin) ? null // assembly_admin: el backend valida
                        : null;
  const sinConjuntos = isAdmin && publicables.length === 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1><i className={`fas fa-bullhorn ${styles.iconoCabecera}`} />Comunicados</h1>
          <p>Avisos para los propietarios del conjunto. Se ven en su panel; opcionalmente se envían por correo.</p>
        </div>
        <div className={styles.acciones}>
          {(isSuperAdmin || (isAdmin && publicables.length > 1)) && (
            <PropertyCombobox value={filterProp} onChange={(id) => setFilterProp(id)} placeholder="— Todos los conjuntos —"
              params={isSuperAdmin ? undefined : { sin_logistica: 1 }} ariaLabel="Conjunto" />
          )}
          {!sinConjuntos && (
            <button className="btn btn-primary" onClick={() => setEditor({})}>
              <i className="fas fa-plus" /> Nuevo comunicado
            </button>
          )}
        </div>
      </div>

      {sinConjuntos && (
        <div className={`alert alert-warning ${styles.avisoSinConjuntos}`}>
          <i className={`fas fa-circle-info ${styles.iconoAviso}`} />
          Ninguno de tus conjuntos tiene un plan que incluya comunicados. El plan
          «Asamblea + Logística» no incluye este servicio.
        </div>
      )}

      {alert && <div className={`alert alert-${alert.type}`}>{alert.message}</div>}
      {error && <div className="alert alert-danger" onClick={() => setError('')}>{error}</div>}

      {items === null ? (
        <div className="loading"><div className="spinner" /><p>Cargando...</p></div>
      ) : items.length === 0 ? (
        <div className="card"><div className="empty-state">
          <i className="fas fa-bullhorn" />
          <p>No hay comunicados{filterProp ? ' para este conjunto' : ''} todavía.</p>
        </div></div>
      ) : (
        <div className={styles.lista}>
          {items.map(a => {
            const st = estado(a);
            const bloqueado = !isSuperAdmin && logisticaIds.has(a.property_id);
            return (
              <div key={a.id} className={`card ${bloqueado ? styles.tarjetaBloqueada : styles.tarjeta}`}>
                <div className={styles.tarjetaFila}>
                  <div className={styles.tarjetaCuerpo}>
                    <div className={styles.etiquetas}>
                      <span className={styles.chipConjunto}>
                        <i className={`fas fa-building ${styles.iconoConjunto}`} />
                        {a.property_name || 'Conjunto'}
                      </span>
                      {a.assembly_number
                        ? <span className={`badge ${styles.etiquetaAsamblea}`}>Para la asamblea #{a.assembly_number}</span>
                        : <span className={`badge ${styles.etiquetaGeneral}`}>General</span>}
                      <span className={`badge ${styles.etiquetaEstado}`} style={{ '--tono': st.color, '--fondo': `${st.color}22` }}>{st.label}</span>
                    </div>
                    <div className={styles.titulo}>{a.title}</div>
                    <div className={styles.meta}>
                      {a.published_at ? `Publicado ${fmtDate(a.published_at)}` : `Creado ${fmtDate(a.created_at)}`}
                      {a.author ? ` · ${a.author}` : ''}
                    </div>
                    <div className={styles.texto}>{a.body}</div>
                  </div>
                </div>

                {bloqueado && (
                  <div className={styles.avisoPlan}>
                    <i className={`fas fa-lock ${styles.iconoPlan}`} />
                    <strong>{a.property_name}</strong> cambió al plan «Asamblea + Logística», que no incluye
                    comunicados. Este comunicado queda como registro; para editarlo o reenviarlo, cambia el plan del conjunto.
                  </div>
                )}

                <div className={styles.botones}>
                  <button className="btn btn-sm btn-secondary" disabled={bloqueado} onClick={() => setEditor(a)}>
                    <i className="fas fa-pencil" /> Editar
                  </button>
                  {!a.published_at ? (
                    <button className="btn btn-sm btn-primary" disabled={bloqueado}
                      onClick={() => act(() => announcementService.publish(a.id, false), 'Comunicado publicado')}>
                      <i className="fas fa-bullhorn" /> Publicar
                    </button>
                  ) : (
                    <button className="btn btn-sm btn-secondary" disabled={bloqueado}
                      onClick={() => act(() => announcementService.unpublish(a.id), 'Comunicado retirado')}>
                      <i className="fas fa-eye-slash" /> Retirar
                    </button>
                  )}
                  {a.published_at && !a.emailed_at && (
                    <button className="btn btn-sm btn-secondary" disabled={bloqueado}
                      onClick={() => setConfirmSend(a)}>
                      <i className="fas fa-envelope" /> Enviar por correo
                    </button>
                  )}
                  <button className={`btn btn-sm btn-danger ${styles.botonEliminar}`} disabled={bloqueado}
                    onClick={() => setConfirmDel(a)}>
                    <i className="fas fa-trash" /> Eliminar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editor && (
        <ComunicadoFormModal
          initial={editor.id ? editor : null}
          properties={publicables}
          fixedPropertyId={filterProp || fixedPropertyId || null}
          asmAdminAssemblies={isAsmAdmin ? myAsms : null}
          onClose={() => setEditor(null)}
          onDone={load}
        />
      )}

      {confirmDel && (
        <ConfirmDeleteModal title="Eliminar comunicado" onClose={() => setConfirmDel(null)} onConfirm={confirmDelete} deleting={busyModal}>
          <div className={styles.confirmaTitulo}>
            ¿Eliminar <span className={styles.confirmaPeligro}>{confirmDel.title}</span>?
          </div>
        </ConfirmDeleteModal>
      )}

      {confirmSend && (
        <ConfirmActionModal title="Enviar por correo" onClose={() => setConfirmSend(null)} onConfirm={confirmSendEmail}
          loading={busyModal} confirmLabel="Sí, enviar" confirmIcon="fa-envelope">
          <div className={styles.confirmaTitulo}>
            ¿Enviar <span className={styles.confirmaAcento}>{confirmSend.title}</span> por correo a todos los propietarios del conjunto?
          </div>
        </ConfirmActionModal>
      )}
    </div>
  );
}
